import React, { useState, useEffect } from 'react';
import IntegrationService from '../../../../services/integration_service';
import FinancialCategoryService from '../../../../services/financial_category_service';
import { useTenant } from '../../../../hooks/useTenant';
import { toast } from 'react-toastify';
import { NeoModal, NeoButton, NeoInput, NeoBadge } from '../../../../components/Common';

/**
 * Componente modal para importar despesas via IA (leitura de extrato)
 */
const PayableAiImportModal = ({ isOpen, toggle, onSaveBatch }) => {
    const { idTenant } = useTenant();
    const [file, setFile] = useState(null);
    const [base64Image, setBase64Image] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState([]);
    const [error, setError] = useState(null);
    const [expenseCategories, setExpenseCategories] = useState([]);

    // Busca categorias para ter o ID delas disponíveis pro AI e pra edição no modal
    useEffect(() => {
        if (isOpen && idTenant) {
            FinancialCategoryService.getAll(idTenant)
                .then(allCats => {
                    const params = allCats.filter(c => c.type === "EXPENSE");
                    setExpenseCategories(params);
                })
                .catch(console.error);
        } else {
            // reset state when modal closes
            setFile(null);
            setBase64Image(null);
            setPreviewUrl(null);
            setAiResults([]);
            setError(null);
        }
    }, [isOpen, idTenant]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setError(null);
        setBase64Image(null);

        const isTextFile = selectedFile.name.endsWith('.csv') || selectedFile.name.endsWith('.ofx') || selectedFile.name.endsWith('.txt');

        const reader = new FileReader();
        reader.onloadend = () => {
            if (isTextFile) {
                // Para arquivos de texto, salvamos o texto do arquivo como se fosse o base64 (iremos usar depois no envio)
                setBase64Image(reader.result);
                setPreviewUrl(null);
            } else {
                setPreviewUrl(reader.result);
                const base64Data = reader.result.split(',')[1];
                setBase64Image(base64Data);
            }
        };

        if (isTextFile) {
            reader.readAsText(selectedFile);
        } else {
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleAnalyze = async () => {
        if (!base64Image || !file) return;

        setIsAnalyzing(true);
        setError(null);

        try {
            console.log("[PayableAiImportModal] Buscando config para tenant:", idTenant);
            // Tenta pegar a integração do Gemini
            let provider = "gemini";
            let integrationConfig = await IntegrationService.getByProvider(idTenant, "gemini");
            console.log("[PayableAiImportModal] Configuração Gemini encontrada:", integrationConfig);
            
            if (!integrationConfig?.config?.apiKey) {
                provider = "openai";
                integrationConfig = await IntegrationService.getByProvider(idTenant, "openai");
                console.log("[PayableAiImportModal] Configuração OpenAI encontrada:", integrationConfig);
                
                if (!integrationConfig?.config?.apiKey) {
                    const allIntegrations = await IntegrationService.getAll(idTenant);
                    console.log("[PayableAiImportModal] Todas as integrações salvas no banco:", allIntegrations);
                    throw new Error("Nenhuma API Key (Gemini ou OpenAI) foi salva no banco de dados. Acesse 'Configurações > Integrações' para preencher a sua API Key e salvar.");
                }
            }

            // Monta o array legível de categorias para mandar pro prompt
            const categoryNames = expenseCategories.map(c => `"${c.name}"`).join(', ');

            const systemPrompt = `
Você é um assistente financeiro sênior especializado em leitura de extratos bancários e comprovantes.
Analise a imagem ou texto do extrato/comprovante fornecido e extraia TODAS as entradas (despesas ou receitas).
Fique atento: em extratos bancários, "Débitos" ou valores negativos são despesas, e "Créditos" ou valores positivos são receitas. Se for uma página de Contas a Pagar, foque nas despesas.
Se for um registro de Pix Enviado, Pagamento de Boleto, etc, considere STATUS como PAID. Se for um agendamento futuro ou boleto a pagar, STATUS como PENDING.

IMPORTANTE: IGNORE valores de "Saldo Anterior" ou "Saldo Atual" do extrato, foque APENAS nos lançamentos diários de entrada e saída. Se o valor for negativo (saída), extraia sempre como um "amount" positivo, pois o tipo da categoria dirá se é despesa.

Retorne OBRIGATORIAMENTE um array de objetos JSON contendo:
[
  {
    "description": "Nome da despesa / Favorecido (ex: Conta de Luz CPFL, João da Silva)",
    "amount": Valor numérico POSITIVO bruto da despesa (ex: 250.50),
    "dueDate": "Data de vencimento ou da transação do extrato, formato YYYY-MM-DD",
    "categoryName": Tente ao máximo enquadrar em exatas UMA das seguintes categorias: ${categoryNames}. Se não achar nenhuma parecida retorne 'Outras Despesas',
    "status": "PAID" ou "PENDING" - Extratos são quase sempre PAID.
    "paymentMethod": Adivinhe o método ("PIX", "TRANSFER", "BOLETO", "CREDIT_CARD", "DEBIT_CARD", "CASH", "OTHER"). Ex: "Pix Recebido" -> PIX.
  }
]
Não use crases. Apenas o array JSON.`;

            const mimeType = file.type; // image/png, image/jpeg, application/pdf (Gemini accepts PDF)
            
            const response = await IntegrationService.parseImageWithAI(provider, integrationConfig.config, systemPrompt, base64Image, mimeType);
            
            if (response.success && Array.isArray(response.data)) {
                // Mapear o categoryName pro categoryId real do sistema
                const mappedResults = response.data.map(item => {
                    const matchedCategory = expenseCategories.find(c => c.name.toLowerCase() === (item.categoryName || "").toLowerCase());
                    return {
                        ...item,
                        amount: Math.abs(item.amount || 0), // Garante que a UI recebe positivo
                        categoryId: matchedCategory ? matchedCategory.id : "",
                        status: item.status || "PENDING", 
                        paymentMethod: item.paymentMethod || "OTHER",
                    };
                });
                
                if (mappedResults.length === 0) {
                     setError("A IA não identificou nenhuma despesa clara na imagem ou extrato.");
                } else {
                     setAiResults(mappedResults);
                }
            } else {
                throw new Error(response.message || "Erro desconhecido ao ler dados via IA.");
            }
        } catch (err) {
            console.error(err);
            setError(err.message || "Falha na comunicação com a API de Integração.");
        } finally {
            setIsAnalyzing(false);
        }
    };

    const handleUpdateResultRow = (index, field, value) => {
        const newResults = [...aiResults];
        newResults[index][field] = value;
        setAiResults(newResults);
    };
    
    const handleRemoveResultRow = (index) => {
        setAiResults(aiResults.filter((_, i) => i !== index));
    };

    const handleSaveAll = async () => {
        // Validação básica
        const invalidRow = aiResults.find(r => !r.description || !r.amount || !r.dueDate || !r.categoryId);
        if (invalidRow) {
            setError("Por favor, preencha todos os campos obrigatórios em todas as linhas (Descrição, Valor, Vencimento e Categoria).");
            return;
        }

        // Envia para o componente pai salvar em lote
        if (onSaveBatch) {
            try {
                const result = await onSaveBatch(aiResults);
                if (result === false) {
                    setError("Erro ao salvar as despesas. Tente novamente.");
                    return;
                }
                toast.success(`✅ ${aiResults.length} despesa(s) importadas com sucesso!`);
                toggle();
            } catch (err) {
                console.error("[PayableAiImportModal] Erro ao salvar lote:", err);
                setError("Erro inesperado ao salvar. Tente novamente.");
            }
        }
    };

    const modalFooterActions = (
        <>
            <NeoButton variant="light" className="text-muted px-4" onClick={toggle}>
                Cancelar
            </NeoButton>
            <NeoButton 
                variant="primary" 
                className="px-5 py-2 fw-bold" 
                onClick={handleSaveAll} 
                disabled={isAnalyzing || aiResults.length === 0}
            >
                <i className="mdi mdi-check-all me-1"></i>
                Aprovar e Salvar Lote ({aiResults.length})
            </NeoButton>
        </>
    );

    return (
        <NeoModal 
            isOpen={isOpen} 
            toggle={toggle} 
            size="xl" 
            centered 
            title="Leitura Inteligente de Extrato (IA)"
            headerIcon="mdi-robot"
            footerActions={modalFooterActions}
        >
            <div className="p-1 bg-light rounded-3">
                {error && (
                    <div className="alert alert-danger p-2 mb-3 font-size-13 border-0 shadow-sm rounded-3">
                        <i className="mdi mdi-alert-circle-outline me-2"></i>{error}
                    </div>
                )}

                <div className="row g-3">
                    <div className="col-md-4 pe-md-4" style={{ borderRight: "1px dashed rgba(72,39,18,0.12)" }}>
                        <h6 className="fw-bold mb-3 text-muted text-uppercase font-size-12" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>1. Enviar Arquivo</h6>
                        <div className="bg-white p-3 rounded-4 border border-dashed mb-3 d-flex flex-column align-items-center justify-content-center text-center shadow-sm" style={{ borderStyle: "dashed" }}>
                            <div className="position-relative w-100 mb-3">
                                <input 
                                    type="file" 
                                    id="extrato-file-upload"
                                    accept="image/jpeg, image/png, application/pdf, text/csv, text/plain, .csv, .ofx, .txt" 
                                    onChange={handleFileChange} 
                                    disabled={isAnalyzing}
                                    className="d-none"
                                />
                                <label 
                                    htmlFor="extrato-file-upload"
                                    className="w-100 p-4 rounded-3 border border-dashed text-center bg-light d-flex flex-column align-items-center justify-content-center"
                                    style={{ borderStyle: "dashed", cursor: isAnalyzing ? "not-allowed" : "pointer", minHeight: "120px" }}
                                >
                                    <i className="mdi mdi-cloud-upload-outline text-primary font-size-32 mb-2"></i>
                                    <span className="fw-bold text-dark font-size-13">
                                        {file ? file.name : "Selecionar Arquivo"}
                                    </span>
                                    <span className="text-muted small mt-1">
                                        PNG, JPG, PDF, CSV, OFX ou TXT
                                    </span>
                                </label>
                            </div>

                            {file && (file.name.endsWith('.csv') || file.name.endsWith('.ofx') || file.name.endsWith('.txt')) ? (
                                <div className="text-center mb-3 w-100 animate__animated animate__fadeIn">
                                    <NeoBadge color="secondary" solid={false} className="p-3 font-size-13 w-100 text-dark bg-light border border-dashed" style={{ borderRadius: "8px" }}>
                                        <i className="mdi mdi-text-box-outline me-1 text-primary"></i> Arquivo de Texto ({file.name.split('.').pop().toUpperCase()})
                                    </NeoBadge>
                                </div>
                            ) : previewUrl && (
                                <div className="text-center mb-3 w-100 animate__animated animate__fadeIn">
                                    {file?.type === "application/pdf" ? (
                                        <NeoBadge color="secondary" solid={false} className="p-3 font-size-13 w-100 text-dark bg-light border border-dashed" style={{ borderRadius: "8px" }}>
                                            <i className="mdi mdi-file-pdf-box me-1 text-danger"></i> Documento PDF
                                        </NeoBadge>
                                    ) : (
                                        <img src={previewUrl} alt="Preview" className="img-fluid rounded-3 border shadow-sm" style={{ maxHeight: '180px', objectFit: 'contain' }} />
                                    )}
                                </div>
                            )}

                            <NeoButton 
                                variant="primary" 
                                className="w-100 fw-bold d-flex align-items-center justify-content-center mt-2" 
                                disabled={!file || isAnalyzing} 
                                loading={isAnalyzing}
                                loadingText="Analisando..."
                                onClick={handleAnalyze}
                            >
                                <i className="mdi mdi-robot me-1 font-size-16" /> Extrair Lançamentos
                            </NeoButton>
                        </div>
                        <p className="font-size-11 text-muted mt-3">
                            O sistema enviará o documento de forma segura para sua inteligência artificial integrada (Gemini ou OpenAI) para extrair automaticamente a descrição, data, valor e classificar a categoria.
                        </p>
                    </div>

                    <div className="col-md-8 ps-md-4">
                        <h6 className="fw-bold mb-3 text-muted text-uppercase font-size-12" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "0.5px" }}>2. Lançamentos Encontrados</h6>
                        
                        {isAnalyzing && (
                            <div className="text-center py-5 px-4 bg-white border border-dashed rounded-4 d-flex flex-column align-items-center justify-content-center h-100 shadow-sm animate__animated animate__fadeIn" style={{ borderStyle: "dashed", minHeight: "300px" }}>
                                <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }} />
                                <h5 className="fw-bold text-dark font-size-16 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Lendo Extrato com Inteligência Artificial</h5>
                                <p className="text-muted font-size-13 mb-0">Isso costuma levar entre 3 a 7 segundos. Por favor, aguarde...</p>
                            </div>
                        )}

                        {!isAnalyzing && aiResults.length === 0 && !error && (
                            <div className="text-center py-5 px-4 text-muted bg-white border border-dashed rounded-4 d-flex flex-column align-items-center justify-content-center h-100 shadow-sm animate__animated animate__fadeIn" style={{ borderStyle: "dashed", minHeight: "300px" }}>
                                <div className="modal-icon-bg bg-primary-soft mb-3" style={{ width: "64px", height: "64px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <i className="mdi mdi-magnify-scan text-primary font-size-32" />
                                </div>
                                <h5 className="fw-bold text-dark font-size-16 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Aguardando Upload de Extrato</h5>
                                <p className="text-muted font-size-13 max-width-320">Selecione o arquivo do extrato ao lado e clique em <strong>Extrair Lançamentos</strong> para iniciar a leitura por inteligência artificial.</p>
                            </div>
                        )}

                        {aiResults.length > 0 && !isAnalyzing && (
                            <div className="bg-white rounded-4 border shadow-sm p-3 animate__animated animate__fadeIn">
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr className="bg-light text-muted text-uppercase font-size-11 fw-bold" style={{ fontFamily: "Outfit, sans-serif", borderBottom: "2px solid rgba(72,39,18,0.12)" }}>
                                                <th className="py-2">Descrição / Favorecido</th>
                                                <th className="py-2" width="14%">Vencimento</th>
                                                <th className="py-2" width="13%">Valor (R$)</th>
                                                <th className="py-2" width="16%">Categoria</th>
                                                <th className="py-2" width="15%">Status</th>
                                                <th className="py-2" width="15%">Meio de Pgto</th>
                                                <th className="py-2 text-center" width="5%"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {aiResults.map((row, idx) => (
                                                <tr key={idx} className="align-middle">
                                                    <td>
                                                        <NeoInput 
                                                            type="text" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.description} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'description', e.target.value)} 
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <NeoInput 
                                                            type="date" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.dueDate} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'dueDate', e.target.value)} 
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <NeoInput 
                                                            type="number" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.amount} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'amount', parseFloat(e.target.value) || 0)} 
                                                            size="sm"
                                                        />
                                                    </td>
                                                    <td>
                                                        <NeoInput 
                                                            type="select" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.categoryId} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'categoryId', e.target.value)}
                                                            size="sm"
                                                        >
                                                            <option value="">(Selecione)</option>
                                                            {expenseCategories.map(c => (
                                                                <option key={c.id} value={c.id}>{c.name}</option>
                                                            ))}
                                                        </NeoInput>
                                                        {!row.categoryId && row.categoryName && <div className="text-danger font-size-10 mt-1">Sugerido: {row.categoryName}</div>}
                                                    </td>
                                                    <td>
                                                        <NeoInput 
                                                            type="select" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.status} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'status', e.target.value)}
                                                            size="sm"
                                                        >
                                                            <option value="PENDING">A Pagar</option>
                                                            <option value="PAID">Pago</option>
                                                        </NeoInput>
                                                    </td>
                                                    <td>
                                                        <NeoInput 
                                                            type="select" 
                                                            className="bg-light border-0 py-1 mb-0" 
                                                            style={{ borderRadius: "6px", fontSize: "0.85rem" }} 
                                                            value={row.paymentMethod} 
                                                            onChange={(e) => handleUpdateResultRow(idx, 'paymentMethod', e.target.value)}
                                                            size="sm"
                                                        >
                                                            <option value="PIX">PIX</option>
                                                            <option value="TRANSFER">TED/DOC</option>
                                                            <option value="BOLETO">Boleto</option>
                                                            <option value="CREDIT_CARD">Cartão Crédito</option>
                                                            <option value="DEBIT_CARD">Cartão Débito</option>
                                                            <option value="CASH">Dinheiro</option>
                                                            <option value="OTHER">Outro</option>
                                                        </NeoInput>
                                                    </td>
                                                    <td className="text-center">
                                                        <NeoButton 
                                                            variant="outline-danger" 
                                                            size="sm" 
                                                            square 
                                                            icon="mdi-delete"
                                                            onClick={() => handleRemoveResultRow(idx)} 
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                                    <div className="text-muted font-size-13">
                                        Total encontrado: <strong className="text-danger" style={{ fontFamily: "Outfit, sans-serif" }}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(aiResults.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0))}
                                        </strong>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </NeoModal>
    );
};

export default PayableAiImportModal;
